import { useState } from 'react'
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
} from '@mui/material'
import { BugReport } from '@mui/icons-material'

import {
    useGetErrorGroupQuery,
    useListErrorGroupsQuery,
    useTriageErrorGroupsMutation,
} from '@/apis/opsApi'
import { groupLabel, type ErrorGroup, type ErrorGroupStatus } from '@/apis/opsTypes'
import { v2ErrorMessage } from '@/apis/v2Mappers'
import { useAppSelector } from '@/app/hooks'
import { useDebounced } from '@/hooks/useDebounced'
import { useProjectId } from '@/hooks/useReportArgs'

import { ReportStatus, fmtDateTime, fmtNum } from './common'

const STATUS_COLOR: Record<ErrorGroupStatus, 'error' | 'warning' | 'default' | 'success' | 'info'> =
    {
        new: 'error',
        regressed: 'warning',
        open: 'info',
        resolved: 'success',
        ignored: 'default',
    }

/** Status filter presets: "unresolved" is what a triage view shows by default. */
const FILTERS: Record<string, ErrorGroupStatus[]> = {
    unresolved: ['new', 'open', 'regressed'],
    resolved: ['resolved'],
    ignored: ['ignored'],
    all: [],
}

function StatusChip({ status }: { status: ErrorGroupStatus }) {
    return <Chip size="small" label={status} color={STATUS_COLOR[status]} variant="outlined" />
}

function GroupDialog({ group, onClose }: { group: ErrorGroup; onClose: () => void }) {
    const projectId = useProjectId()
    const { data, isLoading, error } = useGetErrorGroupQuery({ projectId, id: group.id })
    const g = data?.group ?? group
    return (
        <Dialog open onClose={onClose} maxWidth="md" fullWidth aria-labelledby="error-group-title">
            <DialogTitle id="error-group-title">{groupLabel(g)}</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                    <StatusChip status={g.status} />
                    <Chip size="small" label={`${fmtNum(g.count)} events`} />
                    {g.last_release && <Chip size="small" label={`release ${g.last_release}`} />}
                    {g.sub_tenant && <Chip size="small" label={`sub-tenant ${g.sub_tenant}`} />}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    First seen {fmtDateTime(g.first_seen)}
                    {g.first_release ? ` (${g.first_release})` : ''} · last seen{' '}
                    {fmtDateTime(g.last_seen)}
                    {g.last_path ? ` on ${g.last_path}` : ''}
                    {g.assignee ? ` · assigned to ${g.assignee}` : ''}
                </Typography>
                {g.frames.length > 0 && (
                    <Box
                        component="pre"
                        sx={{
                            fontFamily: 'monospace',
                            fontSize: 12,
                            bgcolor: 'action.hover',
                            p: 1.5,
                            borderRadius: 1,
                            overflowX: 'auto',
                            mb: 2,
                        }}
                    >
                        {g.frames.join('\n')}
                    </Box>
                )}
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Recent events
                </Typography>
                <ReportStatus
                    isLoading={isLoading}
                    error={error}
                    isEmpty={!!data && data.samples.length === 0}
                    emptyTitle="No recent samples"
                    emptyDescription={
                        data?.samples_error ?? 'Samples come from the raw export window.'
                    }
                >
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Time</TableCell>
                                <TableCell>Path</TableCell>
                                <TableCell>Device</TableCell>
                                <TableCell>Country</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(data?.samples ?? []).map((s, i) => (
                                <TableRow key={String(s.event_id ?? i)}>
                                    <TableCell>{fmtDateTime(String(s.time ?? ''))}</TableCell>
                                    <TableCell>{String(s.path ?? '')}</TableCell>
                                    <TableCell>{String(s.device ?? '')}</TableCell>
                                    <TableCell>{String(s.country ?? '')}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </ReportStatus>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    )
}

/**
 * v2 `/errors` — error groups (fingerprinted at ingest) with triage: status filter, search,
 * bulk resolve / ignore / reopen, and a detail dialog with stack frames and recent samples.
 */
export function ErrorGroupsTab() {
    const projectId = useProjectId()
    const subTenant = useAppSelector(s => s.project.subTenant)
    const [filter, setFilter] = useState<keyof typeof FILTERS>('unresolved')
    const [q, setQ] = useState('')
    const [sort, setSort] = useState<'last_seen' | 'first_seen' | 'count'>('last_seen')
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(25)
    const [selected, setSelected] = useState<string[]>([])
    const [open, setOpen] = useState<ErrorGroup | null>(null)
    const search = useDebounced(q.trim())
    const { data, isLoading, isFetching, error } = useListErrorGroupsQuery({
        projectId,
        subTenant,
        status: FILTERS[filter],
        q: search || undefined,
        sort,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
    })
    const [triage, triageState] = useTriageErrorGroupsMutation()
    const groups = data?.groups ?? []
    const allSelected = groups.length > 0 && groups.every(g => selected.includes(g.id))

    const apply = async (status: ErrorGroupStatus) => {
        try {
            await triage({ projectId, ids: selected, status }).unwrap()
            setSelected([])
        } catch {
            // shown from triageState.error
        }
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
                <TextField
                    size="small"
                    select
                    label="Status"
                    value={filter}
                    onChange={e => {
                        setFilter(e.target.value)
                        setPage(0)
                        setSelected([])
                    }}
                    sx={{ minWidth: 150 }}
                >
                    {Object.keys(FILTERS).map(k => (
                        <MenuItem key={k} value={k}>
                            {k[0].toUpperCase() + k.slice(1)}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    size="small"
                    label="Search title"
                    value={q}
                    onChange={e => {
                        setQ(e.target.value)
                        setPage(0)
                    }}
                />
                <TextField
                    size="small"
                    select
                    label="Sort"
                    value={sort}
                    onChange={e => setSort(e.target.value as typeof sort)}
                    sx={{ minWidth: 150 }}
                >
                    <MenuItem value="last_seen">Last seen</MenuItem>
                    <MenuItem value="first_seen">First seen</MenuItem>
                    <MenuItem value="count">Most events</MenuItem>
                </TextField>
                {selected.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
                        <Typography variant="body2" sx={{ alignSelf: 'center' }}>
                            {selected.length} selected
                        </Typography>
                        {(['resolved', 'ignored', 'open'] as const).map(s => (
                            <Button
                                key={s}
                                size="small"
                                variant="outlined"
                                disabled={triageState.isLoading}
                                onClick={() => {
                                    void apply(s)
                                }}
                            >
                                {s === 'resolved'
                                    ? 'Resolve'
                                    : s === 'ignored'
                                      ? 'Ignore'
                                      : 'Reopen'}
                            </Button>
                        ))}
                    </Box>
                )}
            </Box>
            {triageState.error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {v2ErrorMessage(triageState.error)}
                </Alert>
            )}
            <ReportStatus
                isLoading={isLoading}
                error={error}
                isEmpty={!!data && data.total === 0}
                emptyIcon={<BugReport />}
                emptyTitle={filter === 'unresolved' ? 'No unresolved errors' : 'No error groups'}
                emptyDescription="The browser SDK groups uncaught errors by fingerprint (type, message and top frames)."
            >
                <Card sx={{ opacity: isFetching ? 0.7 : 1 }}>
                    <CardContent>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            size="small"
                                            checked={allSelected}
                                            indeterminate={selected.length > 0 && !allSelected}
                                            onChange={e =>
                                                setSelected(
                                                    e.target.checked ? groups.map(g => g.id) : [],
                                                )
                                            }
                                            slotProps={{ input: { 'aria-label': 'Select all' } }}
                                        />
                                    </TableCell>
                                    <TableCell>Error</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="right">Events</TableCell>
                                    <TableCell>Release</TableCell>
                                    <TableCell>Last seen</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {groups.map(g => (
                                    <TableRow key={g.id} hover>
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                size="small"
                                                checked={selected.includes(g.id)}
                                                onChange={e =>
                                                    setSelected(
                                                        e.target.checked
                                                            ? [...selected, g.id]
                                                            : selected.filter(id => id !== g.id),
                                                    )
                                                }
                                                slotProps={{
                                                    input: { 'aria-label': `Select ${g.title}` },
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ maxWidth: 420 }}>
                                            <Button
                                                variant="text"
                                                size="small"
                                                onClick={() => setOpen(g)}
                                                sx={{
                                                    textTransform: 'none',
                                                    p: 0,
                                                    minWidth: 0,
                                                    textAlign: 'left',
                                                    justifyContent: 'flex-start',
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    fontWeight={600}
                                                    noWrap
                                                    title={g.title}
                                                >
                                                    {groupLabel(g)}
                                                </Typography>
                                            </Button>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                noWrap
                                                display="block"
                                            >
                                                {[g.last_path, g.sub_tenant]
                                                    .filter(Boolean)
                                                    .join(' · ')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <StatusChip status={g.status} />
                                        </TableCell>
                                        <TableCell align="right">{fmtNum(g.count)}</TableCell>
                                        <TableCell>{g.last_release ?? '—'}</TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                            {fmtDateTime(g.last_seen)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        <TablePagination
                            component="div"
                            count={data?.total ?? 0}
                            page={page}
                            onPageChange={(_, n) => {
                                setPage(n)
                                setSelected([])
                            }}
                            rowsPerPage={rowsPerPage}
                            rowsPerPageOptions={[25, 50, 100]}
                            onRowsPerPageChange={e => {
                                setRowsPerPage(Number(e.target.value))
                                setPage(0)
                            }}
                        />
                    </CardContent>
                </Card>
            </ReportStatus>
            {open && <GroupDialog group={open} onClose={() => setOpen(null)} />}
        </Box>
    )
}
